/**
 * 상류 서비스 상태 패널(관리자) — S34 Phase 1(상태) + Phase 2(DB 설정 편집).
 *
 * 4개 상류(omni·whisper·asr·correct)가 지금 도달 가능하고 설정한 모델을
 * 올린 채인지만 본다. **번역 품질·부하 성능은 확인하지 않는다** — 하단
 * 고정 문구로 매번 말한다.
 *
 * 배지 축은 FP 0 이 목표다(CLAUDE.md §0-2). `disabled`(스위치로 꺼둔 서비스)
 * 는 절대 빨강이 아니다 — 안 도는 박스를 빨강으로 그리면 진짜 장애의 빨강을
 * 무디게 만든다. `degraded`(model_mismatch)는 설정/박스 모델명을 나란히
 * 보여 GPU 선점 교체를 놓치지 않게 한다.
 *
 * 갱신은 마운트 1회 + 60초 간격 폴링이되, **3회(3분) 후 자동 정지**한다.
 * RoomAdminPanel 의 5초 폴링(in-process 조회)과 다르다 — 이건 관리자 요청
 * 마다 원격 박스 4대를 HTTP 로 찌른다(§2.5), 무한 폴링은 그 부하를 방치한다.
 *
 * ── Phase 2: DB override 편집(§1.5/§2.4/§3) ──────────────────────────────
 *
 * 유효값이 env 와 다를 수 있는 세 가지 경우:
 *   1. `url === env_url` — 드리프트 없음, 아무 표시도 안 한다.
 *   2. 관리자가 **의도적으로** 바꿈(`edited_by !== ''`) — 파란 "관리자 변경"
 *      배지만. 경고 아님(FP 0, CLAUDE.md §0-2) — 관리자가 알고 한 일이다.
 *   3. env 가 seed 이후 바뀌었는데 DB 값에 **가려져 조용히 무시되는 중**
 *      (`edited_by === '' && env_url !== seeded_env`) — 이게 진짜 위험하다:
 *      운영자가 .env 를 고쳤다고 믿는데 실제로는 예전 DB 값이 계속 쓰인다.
 *      노란 경고 배지 + [env 값으로 되돌리기] + 패널 상단 배너.
 *
 * 편집 중에는 폴링을 쉰다(room-admin-panel.tsx 의 busyRef 관행과 동일) —
 * 안 그러면 입력 중인 필드가 갱신에 갈아엎인다.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Badge, type BadgeProps } from '@cupia/ui/badge'
import { Card, CardContent } from '@cupia/ui/card'
import { Spinner } from '@cupia/ui/spinner'
import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { Input } from '@cupia/ui/input'
import { confirm } from '@cupia/ui/confirm-dialog'
import { RefreshCw, Pencil } from 'lucide-react'
import {
  fetchUpstreams,
  saveUpstream,
  resetUpstream,
  testUpstream,
  type UpstreamServiceStatus,
  type UpstreamStatus,
  type UpstreamId,
  type TestUpstreamResponse,
  type SaveUpstreamResponse,
  READ_ONLY_UPSTREAM_IDS,
} from '../lib/upstreams'
import { ApiError } from '../lib/api'

/** 60초 간격, 3회 후 정지(§3 — 원격 박스 4대를 찌르는 조작이라 무한 폴링 금지). */
const POLL_MS = 60_000
const MAX_AUTO_TICKS = 3
/** [지금 확인] 연타 방지 — 서버 하드 레이트리밋(3s)과 짝을 맞춘다(§2.5). */
const FORCE_COOLDOWN_MS = 3000
/** 이 나이(초)를 넘으면 "오래된 값" 을 표시한다. */
const STALE_AGE_S = 60

/** status → 배지 문구·variant. 6개 전부 매핑 — 빠지면 무배지=정상처럼 보인다. */
const STATUS_BADGE: Record<UpstreamStatus, { label: string; variant: BadgeProps['variant'] }> = {
  ok: { label: '정상', variant: 'softSuccess' },
  degraded: { label: '모델 불일치', variant: 'softWarn' },
  starting: { label: '기동 중', variant: 'softWarn' },
  down: { label: '응답 없음', variant: 'softDanger' },
  // disabled 는 장애가 아니라 스위치로 꺼둔 것 — 빨강이면 오탐이다.
  disabled: { label: '꺼짐(스위치)', variant: 'softGray' },
  unknown: { label: '미확인', variant: 'softGray' },
}

export interface UpstreamPanelProps {
  onNotify?: (message: string) => void
}

export function UpstreamPanel({ onNotify }: UpstreamPanelProps): React.ReactElement | null {
  const [data, setData] = useState<{
    checkedAt: number
    ageS: number
    services: UpstreamServiceStatus[]
  } | null>(null)
  const [denied, setDenied] = useState(false)
  const [stopped, setStopped] = useState(false)
  const [forceBusy, setForceBusy] = useState(false)
  // [다시 확인] 을 누르면 이 값을 올려 폴링 useEffect 를 다시 마운트시킨다
  // (3회 정지 → 재개를 "새 폴링 사이클" 로 다룬다).
  const [pollGen, setPollGen] = useState(0)
  const ticksRef = useRef(0)
  // 편집 중이거나(어느 행이든) 시험/저장/되돌리기 요청이 진행 중이면 폴링을
  // 쉰다 — room-admin-panel.tsx 의 busyRef 관행과 동일하게 setInterval 콜백
  // 안에서 load() 호출만 건너뛰고 틱 카운팅·정지 로직은 그대로 둔다.
  const [editingId, setEditingId] = useState<UpstreamId | null>(null)
  const [rowBusy, setRowBusy] = useState(false)
  const busyRef = useRef(false)
  busyRef.current = editingId !== null || rowBusy

  const notify = useCallback((m: string) => onNotify?.(m), [onNotify])

  const load = useCallback(
    async (force: boolean) => {
      try {
        const res = await fetchUpstreams(force)
        // 응답 모양을 믿지 않는다 — 이 패널은 관리 화면 안에 얹혀 있어서,
        // 여기서 던지면 계정 관리까지 통째로 죽는다(room-admin-panel.tsx 와
        // 같은 규율).
        if (!res || !Array.isArray(res.services)) {
          // 빈 배열이라도 확정 상태로 남긴다 — 그러지 않으면 로딩 스피너가
          // 영원히 떠 있게 되고, 관리 화면 다른 곳의 role="status" 조회와
          // 충돌한다(room-admin-panel.tsx 의 빈 배열 폴백과 같은 규율).
          setData({ checkedAt: 0, ageS: 0, services: [] })
          notify('상류 상태를 불러오지 못했습니다.')
          return
        }
        setData({
          checkedAt: res.checked_at ?? 0,
          ageS: typeof res.age_s === 'number' ? res.age_s : 0,
          services: res.services,
        })
        setDenied(false)
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          // 관리자가 아니면 상류 상태를 볼 이유가 없다 — 조용한 빈 상태.
          setDenied(true)
          setData(null)
          return
        }
        notify('상류 상태를 불러오지 못했습니다.')
      }
    },
    [notify],
  )

  useEffect(() => {
    void load(false)
    ticksRef.current = 0
    setStopped(false)
    const id = setInterval(() => {
      ticksRef.current += 1
      if (!busyRef.current) void load(false)
      if (ticksRef.current >= MAX_AUTO_TICKS) {
        clearInterval(id)
        setStopped(true)
      }
    }, POLL_MS)
    return () => clearInterval(id)
  }, [load, pollGen])

  const onForceRefresh = useCallback(() => {
    if (forceBusy) return
    setForceBusy(true)
    void load(true).finally(() => {
      setTimeout(() => setForceBusy(false), FORCE_COOLDOWN_MS)
    })
  }, [forceBusy, load])

  /** [다시 확인] — 자동 갱신이 멈춘 뒤 수동으로 폴링을 다시 튼다. */
  const onResumeAuto = useCallback(() => {
    setStopped(false)
    setPollGen((g) => g + 1)
  }, [])

  if (denied) return null

  if (data === null) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-4 text-[var(--color-muted-foreground)]">
          <Spinner className="h-4 w-4" />
          상류 상태를 불러오는 중…
        </CardContent>
      </Card>
    )
  }

  const stale = data.ageS > STALE_AGE_S
  // §1.5 세 번째 분기(조용히 무시되는 env 드리프트)에 걸린 행 수 — 상단 배너용.
  // F-C2 — 표류 판정은 URL 만이 아니라 모델명도 본다(env_drift || model_drift).
  const driftHiddenCount = data.services.filter(
    (s) => (s.env_drift || s.model_drift) && s.edited_by === '' && s.env_url !== s.seeded_env,
  ).length

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">상류 서비스 상태</span>
          <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            {formatCheckedAt(data.checkedAt)} · {data.ageS.toFixed(1)}초 전
            {stale && ' · 오래된 값'}
          </span>
          <div className="ms-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={forceBusy}
              onClick={onForceRefresh}
              data-testid="upstream-force-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              지금 확인
            </Button>
          </div>
        </div>

        {driftHiddenCount > 0 && (
          <Alert variant="warning" data-testid="upstream-drift-banner">
            <AlertTitle>env 설정이 DB 값에 가려져 있습니다</AlertTitle>
            <AlertDescription>
              env 설정 {driftHiddenCount}건이 DB 값에 가려져 적용되지 않고 있습니다.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2" data-testid="upstream-rows">
          {data.services.map((s) => (
            <UpstreamRow
              key={s.id}
              service={s}
              editing={editingId === s.id}
              onToggleEdit={() => setEditingId((cur) => (cur === s.id ? null : s.id))}
              onBusyChange={setRowBusy}
              onSaved={() => {
                setEditingId(null)
                void load(false)
              }}
              notify={notify}
            />
          ))}
        </div>

        {stopped && (
          <div className="flex items-center gap-2 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            자동 갱신이 멈췄습니다.
            <Button
              variant="ghost"
              size="sm"
              onClick={onResumeAuto}
              data-testid="upstream-resume-auto"
            >
              다시 확인
            </Button>
          </div>
        )}

        <p className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          이 점검은 도달·모델 적재 여부만 확인합니다 — 번역 품질과 부하 시 성능은
          확인하지 않습니다.
        </p>
      </CardContent>
    </Card>
  )
}

interface UpstreamRowProps {
  service: UpstreamServiceStatus
  editing: boolean
  onToggleEdit: () => void
  onBusyChange: (busy: boolean) => void
  onSaved: () => void
  notify: (m: string) => void
}

/** 저장 응답의 note 를 §2.4 안내문에 그대로 이어붙인다 — 서버 계산치를 잃지 않는다. */
function formatSaveNote(id: UpstreamId, res: SaveUpstreamResponse): string {
  const prefix =
    res.restart_required || id === 'omni'
      ? '저장했습니다 — 재기동 후 적용됩니다.'
      : '저장했습니다 — 다음 방 개설부터 적용됩니다.'
  const live = !res.restart_required && res.live_rooms > 0
    ? ` (진행 중인 방 ${res.live_rooms}개는 기존 값을 유지합니다)`
    : ''
  return `${prefix}${live} ${res.note}`.trim()
}

function UpstreamRow({
  service: s,
  editing,
  onToggleEdit,
  onBusyChange,
  onSaved,
  notify,
}: UpstreamRowProps): React.ReactElement {
  const badge = STATUS_BADGE[s.status]
  const [urlInput, setUrlInput] = useState(s.url)
  const [modelInput, setModelInput] = useState(s.model)
  // 마지막 [시험] 이 어떤 입력값으로, 어떤 결과로 끝났는지 — 저장 시 재확인용.
  const [testResult, setTestResult] = useState<TestUpstreamResponse | null>(null)
  const [testedFor, setTestedFor] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)

  // 편집 진입 시 현재 유효값으로 초기화.
  useEffect(() => {
    if (editing) {
      setUrlInput(s.url)
      setModelInput(s.model)
      setTestResult(null)
      setTestedFor(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  // §1.5 세 갈래. F-C2 — 표류 있음 판정은 URL 뿐 아니라 모델명도 본다(§3).
  const drifted = s.env_drift || s.model_drift
  const noDrift = !drifted
  const adminChanged = !noDrift && s.edited_by !== ''
  const hiddenDrift = !noDrift && s.edited_by === '' && s.env_url !== s.seeded_env

  const inputKey = `${urlInput} ${modelInput}`

  const runTest = useCallback(async () => {
    setTesting(true)
    onBusyChange(true)
    try {
      const res = await testUpstream(s.id, urlInput, modelInput)
      setTestResult(res)
      setTestedFor(inputKey)
    } catch (e) {
      notify(e instanceof ApiError ? e.message : '시험 요청이 실패했습니다.')
    } finally {
      setTesting(false)
      onBusyChange(false)
    }
  }, [s.id, urlInput, modelInput, inputKey, onBusyChange, notify])

  const doSave = useCallback(async () => {
    onBusyChange(true)
    try {
      const res = await saveUpstream(s.id, urlInput, modelInput)
      notify(formatSaveNote(s.id, res))
      onSaved()
    } catch (e) {
      notify(e instanceof ApiError ? e.message : '저장에 실패했습니다.')
    } finally {
      onBusyChange(false)
    }
  }, [s.id, urlInput, modelInput, onBusyChange, notify, onSaved])

  const onSave = useCallback(async () => {
    const passed = testedFor === inputKey && testResult?.status === 'ok'
    if (!passed) {
      const ok = await confirm({
        title: '시험하지 않은 값을 저장할까요?',
        description:
          testedFor === inputKey
            ? '방금 시험이 실패했습니다. 이 값으로 저장하면 상류가 응답하지 않을 수 있습니다.'
            : '이 값으로 시험을 실행하지 않았습니다. 시험 없이 저장하면 상류가 응답하지 않을 수 있습니다.',
        confirmLabel: '그래도 저장',
        cancelLabel: '취소',
        variant: 'default',
      })
      if (!ok) return
    }
    await doSave()
  }, [testedFor, inputKey, testResult, doSave])

  const onRevert = useCallback(async () => {
    const ok = await confirm({
      title: 'env 값으로 되돌릴까요?',
      description: 'DB 에 저장한 값을 지우고 현재 env 설정으로 되돌립니다. 되돌릴 수 없습니다.',
      confirmLabel: '되돌리기',
      cancelLabel: '취소',
      variant: 'destructive',
    })
    if (!ok) return
    onBusyChange(true)
    try {
      const res = await resetUpstream(s.id)
      notify(formatSaveNote(s.id, res))
      onSaved()
    } catch (e) {
      notify(e instanceof ApiError ? e.message : '되돌리기에 실패했습니다.')
    } finally {
      onBusyChange(false)
    }
  }, [s.id, onBusyChange, notify, onSaved])

  // DEF-050 AMENDMENT #2 B3 — 표시 전용 서비스는 편집·시험 버튼을 아예
  // 안 보여준다(서버는 read_only_service 로 거절하지만, UI 가 먼저
  // 편집 가능처럼 보여주면 안 된다).
  const readOnly = READ_ONLY_UPSTREAM_IDS.has(s.id)

  return (
    <div
      className="flex flex-col gap-0.5 border-b border-[var(--color-border)] py-2 last:border-0"
      data-testid={`upstream-row-${s.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <Badge variant="softGray" data-testid={`upstream-source-${s.id}`}>
          {s.source}
        </Badge>
        <span className="font-medium">{s.label}</span>
        {s.model && (
          <span className="text-[var(--color-muted-foreground)]">· {s.model}</span>
        )}
        <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          {s.url}
        </span>
        <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          {s.latency_ms === null ? '—' : `${s.latency_ms}ms`}
        </span>
        {adminChanged && (
          <Badge variant="soft" data-testid={`upstream-admin-changed-${s.id}`}>
            관리자 변경
          </Badge>
        )}
        {s.pending_restart && (
          <Badge variant="softWarn" data-testid={`upstream-pending-restart-${s.id}`}>
            저장됨 · 재기동 필요
          </Badge>
        )}
        {hiddenDrift && (
          <>
            <Badge variant="softWarn" data-testid={`upstream-drift-warn-${s.id}`}>
              env 변경 미반영
            </Badge>
            <Button variant="outline" size="sm" onClick={onRevert}>
              env 값으로 되돌리기
            </Button>
          </>
        )}
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleEdit}
            data-testid={`upstream-edit-toggle-${s.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {s.status === 'disabled' && s.disabled_reason && (
        <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          꺼짐({s.disabled_reason})
        </span>
      )}
      {s.status === 'degraded' && (
        <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          설정 {s.model} / 박스 {s.models_seen[0] ?? '(없음)'}
        </span>
      )}
      {s.status === 'down' && s.detail && (
        <span className="text-(length:--text-xs-size) text-[var(--color-destructive)]">
          {s.detail}
        </span>
      )}
      {editing && !readOnly && (
        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid={`upstream-edit-${s.id}`}>
          <Input
            size="sm"
            value={urlInput}
            onValueChange={setUrlInput}
            placeholder="URL"
            aria-label={`${s.label} URL`}
          />
          <Input
            size="sm"
            value={modelInput}
            onValueChange={setModelInput}
            placeholder="모델명"
            aria-label={`${s.label} 모델명`}
          />
          <Button variant="outline" size="sm" disabled={testing} onClick={() => void runTest()}>
            시험
          </Button>
          {testedFor === inputKey && testResult && (
            <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
              {STATUS_BADGE[testResult.status].label}
              {testResult.latency_ms !== null && ` · ${testResult.latency_ms}ms`}
              {testResult.detail && ` · ${testResult.detail}`}
            </span>
          )}
          <Button variant="default" size="sm" onClick={() => void onSave()}>
            저장
          </Button>
        </div>
      )}
    </div>
  )
}

function formatCheckedAt(epochS: number): string {
  if (!epochS) return '—'
  return new Date(epochS * 1000).toLocaleTimeString('ko-KR', { hour12: false })
}
